import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
// Faz 4: Birincil kimlik = own-auth (gts_auth_api). Firebase Auth ARTIK SADECE
// Google ID token KAYNAĞI olarak kullanılır (signInWithPopup → idToken çıkar →
// hemen firebaseSignOut). Firebase oturumu TUTULMAZ.
import {
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { auth } from '../firebase';
import * as ownAuthSession from '../services/auth/ownAuthSession';
import type { OwnAuthUser } from '../services/auth/ownAuthSession';
import { accountingApi, mainApi } from '../config/apiClient';
import { ACCOUNTING_API } from '../config/api';
import { setMigrationMode, getMigrationMode, type MigrationMode } from '../services/migration/migrationMode';
import { getBranches as getBranchesAdapter } from '../services/migration/branchDataService';
import { getPersonnel as getPersonnelAdapter, getAdminPanel } from '../services/migration/personnelDataService';
import type { AdminPermissionsMap } from '../components/users/adminModuleMap';
import firebaseDataService from '../services/FirebaseDataService';
import { isRunningInFlutter, notifyFlutterLogout } from '../utils/flutterBridge';

// User details tipini tanımla
export interface UserDetails {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  companyId?: string;
  /**
   * Kullanıcının üye olduğu firmanın adı (sidebar/menü gösterimi için).
   * ⚠️ Fatura/fiş "satıcı" alanı için kullanılmaz — `SellerResolver` (backend)
   * Companies vs Branches.Firm* karar mantığını yürütür. Bkz. `sellerInfoService.ts`.
   */
  companyName?: string;
  role: string;
  createdAt: number;
  branchesId?: string;
  photoURL?: string | null;
  kvkkAccepted?: boolean;
}

// Şube, Kategori, Marka, Depo tipleri
export interface Branch {
  id: string;
  name: string;
  address?: string;
  phone?: string;
  isHeadquarter?: boolean;
}

export interface Category {
  id: string;
  name: string;
  parentId: string | null;
  level: number;
  createdAt: string;
  branchesId?: string | null;
}

export interface Brand {
  id: string;
  name: string;
  description?: string;
  website?: string;
}

export interface Warehouse {
  id: string;
  name: string;
  address?: string;
  description?: string;
  isDefault?: boolean;
  warehouseType?: string;
  locationModel?: string;
  createdAt?: string;
  branchesId?: string | null;
  stockCount?: number;
  activeItemCount?: number;
}

// ✅ Personel Cache için tip
export interface PersonnelInfo {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  role?: string;
  branchesId?: string | null;
  departmentId?: string | null;
  companyId?: string;
}

// Cache için tip
interface CachedData<T> {
  data: T;
  timestamp: number;
  companyId: string;
}

// Google kullanıcı verilerini tanımla
export interface GoogleUserData {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
}

/**
 * COMPAT SHIM — Faz 4 (Firebase Auth → own-auth geçişi).
 *
 * Firebase `User` yerine geçer. ~100 downstream bileşen `currentUser.uid`,
 * `currentUser.email`, `currentUser.getIdToken()` çağrılarını DEĞİŞTİRMEDEN
 * kullanmaya devam eder. `getIdToken` artık own access token döndürür
 * (Firebase ID token değil) ve hata durumunda boş string'e düşer.
 */
export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  companyId?: string;
  /**
   * ⚠️ Own-auth'ta DOLDURULMAZ (her zaman undefined). Firebase'e özgü provider
   * tespiti yapan bileşenler (`ManagerDashboard.tsx`, `layout/Header.tsx` —
   * `providerData.some(p => p.providerId === 'password')`) bu yüzden artık
   * `false` görecektir. FOLLOW-UP: bu kontroller userDetails/role tabanlı
   * mantığa taşınmalı.
   */
  providerData?: Array<{ providerId: string }>;
  getIdToken: (force?: boolean) => Promise<string>;
}

// Dahili takma ad — mevcut `User` referanslarını (fetchUserDetails vb.) korur.
type User = AuthUser;

/** own-auth User nesnesinden compat AuthUser shim'i üretir. */
function buildAuthUser(u: OwnAuthUser): AuthUser {
  const fullName = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
  return {
    // Kanonik kimlik = firebaseUid (SQL getUserData(uid) bunu bekler).
    uid: u.firebaseUid || u.id,
    email: u.email ?? null,
    displayName: fullName || null,
    photoURL: (u.photoURL as string | null | undefined) ?? null,
    companyId: u.companyId,
    getIdToken: async () => (await ownAuthSession.getAccessToken()) || '',
  };
}

interface AuthContextType {
  currentUser: User | null;
  userDetails: UserDetails | null;
  login: (email: string, password: string) => Promise<AuthUser>;
  googleLogin: () => Promise<AuthUser>;
  googleLoginPopup: () => Promise<AuthUser>;
  googleRedirectResult: () => Promise<AuthUser | null>;
  loginApple: (identityToken: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
  /**
   * 🎭 Süper admin → kullanıcı taklit (impersonation). Hedef kullanıcı için yeni
   * oturum kurar, userDetails'i yükler ve tenant cache'lerini sıfırlar.
   */
  impersonate: (target: { targetUserId?: number; targetEmail?: string }) => Promise<AuthUser>;
  /** Impersonation'ı sonlandırır ve yedeklenen süper admin oturumuna geri döner. */
  stopImpersonation: () => Promise<void>;
  /** Şu an bir kullanıcı taklit oturumunda mıyız? */
  isImpersonating: boolean;
  /** Aktif impersonation özeti (hedef kullanıcı + gerçek aktör e-postası) veya null. */
  impersonationInfo: { targetUser: OwnAuthUser | null; actorEmail: string | null } | null;
  loading: boolean;
  refreshUserDetails: (userOverride?: User) => Promise<void>;
  setGoogleUserData: (userData: GoogleUserData) => void;
  googleUserData: GoogleUserData | null;
  /**
   * Kullanıcının ana firmasının adı.
   *
   * ⚠️ PR3 (Invoice Seller Snapshot Refactor) — KULLANIM SINIRLARI:
   * Bu alan SADECE genel UI gösterimi için kullanılır (sidebar başlığı, menü/header,
   * fatura numarası öneki vb.). Burada saklanan değer kullanıcının ÜYE OLDUĞU
   * şirketin adıdır; fatura kesilen "satıcı şirket" anlamına GELMEZ.
   *
   * Fatura kayıt veya fiş yazdırma sırasında satıcı (seller) bilgisi olarak
   * kullanılmamalıdır. Doğru satıcı snapshot'ı backend `SellerResolver` tarafından
   * `SellerInfoController` üzerinden gelir (Companies vs Branches.Firm* —
   * franchise şubeler için kritik). Bkz:
   *   - `gts-web/src/services/sellerInfoService.ts`
   *   - `gts_dotnetapi/GTS.Business/Managers/Accounting/SellerResolver.cs`
   */
  companyName?: string;
  // Merkezi Cache Sistemi
  branches: Branch[];
  categories: Category[];
  brands: Brand[];
  warehouses: Warehouse[];
  loadBranches: (forceRefresh?: boolean) => Promise<void>;
  loadCategories: (forceRefresh?: boolean) => Promise<void>;
  loadBrands: (forceRefresh?: boolean) => Promise<void>;
  loadWarehouses: (forceRefresh?: boolean) => Promise<void>;
  getBranchName: (branchId: string | null | undefined) => string;
  getCategoryName: (categoryId: string | null | undefined) => string;
  getBrandName: (brandId: string | null | undefined) => string;
  getWarehouseName: (warehouseId: string | null | undefined) => string;
  clearAllCaches: () => void;
  refreshAllCaches: () => Promise<void>;
  // ✅ Personel Cache Sistemi
  personnelList: PersonnelInfo[];
  loadPersonnelList: () => Promise<void>;
  getPersonnelInfo: (personnelId: string) => PersonnelInfo | null;
  getPersonnelName: (personnelId: string | null | undefined) => string;
  /**
   * Firebase->SQL gecis modu (sirket bazli). 'HybridFallback' (default) veya 'SqlOnly'.
   * `/api/Company` cevabindan yuklenir; modul-seviyesi cache'e de yazilir
   * (hook disindaki servisler `getMigrationMode()` ile okur).
   */
  migrationMode: MigrationMode;
  /**
   * Sirketin acik modulleri (SuperAdmin modul kontrolu, Companies.EnabledModulesJson).
   * null = kisitlama yok (tum moduller acik). `/api/Company` cevabindan yuklenir.
   */
  enabledModules: string[] | null;
  /** Modul acik mi? enabledModules=null iken her modul aciktir. */
  isModuleEnabled: (moduleId: string) => boolean;
  /**
   * Demo bitis tarihi (ISO string, `/api/Company` yanitindan). null = demo degil (suresiz).
   * Uyari bandi kalan gunu bundan hesaplar; kilit karari ise SUNUCUNUN
   * hesapladigi demoExpired'a dayanir (istemci saati olcu alinmaz).
   */
  demoUntil: string | null;
  /** Demo suresi doldu mu? Sunucuda UTC ile hesaplanir; true ise MainLayout kilit ekrani gosterir. */
  demoExpired: boolean;
  /** Kullanici super admin mi? (userDetails.role === 'superadmin') */
  isSuperAdmin: boolean;
  /** Kullanici bayi (dealer) mi? Bayiler super admin panelinin kisitli halini kullanir. */
  isDealer: boolean;
  /** Panel yoneticisi mi? (superadmin VEYA dealer — /superadmin rotalarina girebilir) */
  isPanelAdmin: boolean;
  /** Kullanici alt admin mi? (userDetails.role === 'subadmin') */
  isSubAdmin: boolean;
  /**
   * Alt admin (subadmin) modul izin haritasi (SystemUserScreenPermissions,
   * ScreenType='adminPanel'). Yalniz role==='subadmin' iken yuklenir; diger
   * rollerde null. Yuklenemezse {} (deny-by-default: hicbir modul izinli degil).
   */
  adminPanelPermissions: AdminPermissionsMap | null;
  /** adminPanelPermissions henuz yukleniyor mu? (subadmin rota guard'i bekletmek icin) */
  adminPanelLoading: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  return useContext(AuthContext) as AuthContextType;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false);
  const [googleUserData, setGoogleUserData] = useState<GoogleUserData | null>(null);
  const [migrationMode, setMigrationModeState] = useState<MigrationMode>(getMigrationMode());
  // Sirket bazli acik moduller (null = kisitlama yok). /api/Company'den yuklenir.
  const [enabledModules, setEnabledModules] = useState<string[] | null>(null);
  // Demo kullanim siniri (/api/Company'den; demoExpired sunucuda hesaplanir).
  const [demoUntil, setDemoUntil] = useState<string | null>(null);
  const [demoExpired, setDemoExpired] = useState(false);
  // Alt admin (subadmin) modul izin haritasi — yalniz role==='subadmin' iken yuklenir.
  const [adminPanelPermissions, setAdminPanelPermissions] = useState<AdminPermissionsMap | null>(null);
  const [adminPanelLoading, setAdminPanelLoading] = useState(false);
  
  // Merkezi Cache State'leri
  const [branches, setBranches] = useState<Branch[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [personnelList, setPersonnelList] = useState<PersonnelInfo[]>([]);

  // E-posta/şifre ile giriş — own-auth (gts_auth_api).
  async function login(email: string, password: string): Promise<AuthUser> {
    const ownUser = await ownAuthSession.login(email, password);
    const authUser = buildAuthUser(ownUser);
    setCurrentUser(authUser);
    // userDetails'i SQL'den (firebaseDataService) yükle + localStorage'a yaz.
    await fetchUserDetails(authUser);
    return authUser;
  }

  // Firebase'i SADECE Google ID token kaynağı olarak kullanan ortak akış.
  // Token alınır alınmaz Firebase oturumu kapatılır; kimlik own-auth'tadır.
  async function loginWithGoogleToken(): Promise<AuthUser> {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    const result = await signInWithPopup(auth, provider);
    const googleIdToken = GoogleAuthProvider.credentialFromResult(result)?.idToken;
    // Firebase oturumunu KORUMA — yalnızca Google token'ı için kullandık.
    try {
      await firebaseSignOut(auth);
    } catch {
      /* yok say */
    }
    if (!googleIdToken) {
      throw new Error('Google ID token alınamadı.');
    }
    const ownUser = await ownAuthSession.loginGoogle(googleIdToken);
    const authUser = buildAuthUser(ownUser);
    setCurrentUser(authUser);
    await fetchUserDetails(authUser);
    return authUser;
  }

  // Google ile giriş (eski "redirect" imzası korunur; artık popup+own kullanır).
  async function googleLogin(): Promise<AuthUser> {
    return loginWithGoogleToken();
  }

  // Google ile giriş (popup imzası korunur).
  async function googleLoginPopup(): Promise<AuthUser> {
    return loginWithGoogleToken();
  }

  // Redirect akışı own-auth'ta kullanılmıyor; API uyumluluğu için stub.
  async function googleRedirectResult(): Promise<AuthUser | null> {
    return null;
  }

  // Apple ile giriş — own-auth (gts_auth_api). identityToken çağıran tarafça sağlanır.
  async function loginApple(identityToken: string): Promise<AuthUser> {
    const ownUser = await ownAuthSession.loginApple(identityToken);
    const authUser = buildAuthUser(ownUser);
    setCurrentUser(authUser);
    await fetchUserDetails(authUser);
    return authUser;
  }

  // ───────────────────────────────────────────────────────────────────────────
  // 🎭 IMPERSONATION (süper admin → kullanıcı taklit)
  // Geçişte companyId değişeceğinden, MULTI-TENANT sızıntıyı önlemek için in-memory
  // cache state'leri sıfırlanır. companyId-değişim useEffect'i ardından taze veri yükler.
  // ───────────────────────────────────────────────────────────────────────────
  function resetTenantState() {
    setBranches([]);
    setCategories([]);
    setBrands([]);
    setWarehouses([]);
    setPersonnelList([]);
    setLastLoadedCompanyId(null);
  }

  async function impersonate(target: { targetUserId?: number; targetEmail?: string }): Promise<AuthUser> {
    const ownUser = await ownAuthSession.impersonate(target);
    resetTenantState();
    const authUser = buildAuthUser(ownUser);
    setCurrentUser(authUser);
    await fetchUserDetails(authUser);
    return authUser;
  }

  async function stopImpersonation(): Promise<void> {
    const ownUser = ownAuthSession.stopImpersonation();
    resetTenantState();
    if (ownUser) {
      const authUser = buildAuthUser(ownUser);
      setCurrentUser(authUser);
      // ⚠️ KRİTİK: userDetails (rol) GÜNCELLENMEDEN yönlendirme yapılırsa, hedef
      // ekranın yetki guard'ı (ör. SuperAdmin role!=='superadmin') login'e atar.
      // Bu yüzden çağıran (FAB) yönlendirmeden ÖNCE bunu bekler.
      await fetchUserDetails(authUser);
    }
  }

  async function logout() {
    // ───────────────────────────────────────────────────────────────────────────
    // KRİTİK MULTI-TENANT GÜVENLİK
    // Logout sırasında localStorage'da firma bağımlı veriler (userDetails, cache,
    // wms_* anahtarları) BIRAKILIRSA, sonraki kullanıcı login olduğunda fetchUserDetails
    // tamamlanana kadar API istekleri stale companyId ile gider → A firmasının verileri
    // B firmasında görünür. Bu yüzden Firebase signOut'tan ÖNCE tüm tenant-bağımlı
    // state ve localStorage temizlenir.
    // ───────────────────────────────────────────────────────────────────────────
    console.log('🚪 [AUTH] Çıkış yapılıyor — tenant izolasyonu için tüm cache temizleniyor');

    try {
      const previousCompanyId = userDetails?.companyId;

      // 1) Şirket-bağımlı cache anahtarları (eski companyId)
      if (previousCompanyId) {
        localStorage.removeItem(`gts_branches_${previousCompanyId}`);
        localStorage.removeItem(`gts_categories_${previousCompanyId}`);
        localStorage.removeItem(`gts_brands_${previousCompanyId}`);
        localStorage.removeItem(`gts_warehouses_${previousCompanyId}`);
        localStorage.removeItem(`gts_personnel_${previousCompanyId}`);
      }

      // 2) Tenant-bağımlı global anahtarlar
      localStorage.removeItem('userDetails');
      localStorage.removeItem('__customerDisplayAuthToken');
      localStorage.removeItem('__customerDisplayCompanyId');

      // 3) Firma bazlı / firma çapraz sızıntı riski olan tüm prefix'leri tara ve sil
      //    (gts_*_${companyId} ve wms_* anahtarları)
      try {
        const tenantPrefixes = [
          'gts_branches_',
          'gts_categories_',
          'gts_brands_',
          'gts_warehouses_',
          'gts_personnel_',
          'wms_map_obstacles_',
          'wms_map_size_',
          'wms_wave_status',
          'wms_wave_created',
          'wms_order_source_overrides',
          'gts_pending_atomic_payment',
        ];
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i);
          if (!k) continue;
          if (tenantPrefixes.some((p) => k.startsWith(p))) {
            keysToRemove.push(k);
          }
        }
        keysToRemove.forEach((k) => localStorage.removeItem(k));
      } catch (storageErr) {
        console.warn('[AUTH] localStorage tarama hatası:', storageErr);
      }

      // 4) State sıfırlama — logout sonrası bir mikro-saniye içinde stale data render edilmesin
      setCurrentUser(null);
      setUserDetails(null);
      setBranches([]);
      setCategories([]);
      setBrands([]);
      setWarehouses([]);
      setPersonnelList([]);
      setLastLoadedCompanyId(null);
    } catch (e) {
      console.warn('[AUTH] Logout temizlik hatası:', e);
    }

    // 5) own-auth oturumunu sonlandır (best-effort /logout + token temizliği).
    //    Firebase oturumu zaten tutulmuyor; ek signOut gerekmiyor.
    if (isRunningInFlutter()) {
      console.log('🚪 [AUTH] Flutter ortamında own-auth logout...');
      await ownAuthSession.logout();
      notifyFlutterLogout();
      return;
    }

    console.log('🚪 [AUTH] Web ortamında own-auth logout...');
    await ownAuthSession.logout();
  }

  // Kullanıcı detaylarını getir
  // Faz A/D: kimlik tek kaynak = SQL (gts_auth_api / SystemUsers). CF + RTDB fallback kaldirildi.
  async function fetchUserDetails(user: User) {
    if (!user || !user.uid) {
      console.error("fetchUserDetails çağrıldı ancak user veya user.uid tanımsız.");
      setUserDetails(null);
      localStorage.removeItem('userDetails');
      return;
    }
    
    try {
      // SQL'den (gts_auth_api) kullanici verisi cek
      console.log('[AuthContext] 📡 SQL ile kullanıcı verisi çekiliyor...');
      const userData = await firebaseDataService.getUserData(user.uid);
      
      if (userData) {
        const userDetailsData: UserDetails = {
          id: user.uid,
          email: userData.email || user.email || '',
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          companyId: userData.companyId,
          companyName: userData.companyName,
          role: userData.role || 'user',
          createdAt: userData.createdAt || Date.now(),
          branchesId: userData.branchesId,
          photoURL: userData.photoURL,
          kvkkAccepted: userData.kvkkAccepted
        };
        
        console.log('[AuthContext] SQL ile UserDetails yuklendi:', {
          uid: user.uid,
          email: userDetailsData.email,
          role: userDetailsData.role,
          companyId: userDetailsData.companyId
        });

        // NOT (Faz 4): Firebase getIdToken(true)/getIdTokenResult claim yenileme
        // bloğu kaldırıldı. Own-auth JWT claim'leri (companyId, role) sunucu
        // tarafında üretilir ve apiClient `decodeJwt` ile okur.

        setUserDetails(userDetailsData);
        localStorage.setItem('userDetails', JSON.stringify(userDetailsData));
        return;
      }
      
      console.warn('[AuthContext] ⚠️ Kullanıcı verisi (SQL) boş döndü.');
      setUserDetails(null);
      localStorage.removeItem('userDetails');

    } catch (error) {
      // Faz A: kimlik tek kaynak = SQL (gts_auth_api). Eski direkt RTDB users/{uid}
      // fallback'i kaldirildi.
      console.error('[AuthContext] Kullanıcı detayları yüklenirken hata (SQL):', error);
      setUserDetails(null);
      localStorage.removeItem('userDetails');
    }
  }

  // Yeni refreshUserDetails fonksiyonu
  // userOverride parametresi: signInWithCustomToken sonrası henüz React state güncellenmeden önce
  // userCredential.user kullanarak userDetails yüklemek için
  async function refreshUserDetails(userOverride?: User): Promise<void> {
    // own-auth: fallback olarak localStorage'daki stored user'dan shim üret.
    const storedUser = ownAuthSession.getStoredUser();
    const user = userOverride || currentUser || (storedUser ? buildAuthUser(storedUser) : null);
    if (user) {
      console.log("refreshUserDetails çağrıldı, user mevcut:", user.uid);
      await fetchUserDetails(user);
    } else {
      console.warn("refreshUserDetails çağrıldı ancak user null (currentUser ve stored user yok).");
    }
  }

  // ============================================================================
  // MERKEZİ CACHE SİSTEMİ
  // ============================================================================

  const CACHE_DURATION = 24 * 60 * 60 * 1000; // 1 gün (milisaniye)

  /**
   * Cache'den veri okur, expire kontrolü yapar
   */
  function getCachedData<T>(key: string, companyId: string): T | null {
    try {
      const cached = localStorage.getItem(key);
      if (!cached) return null;

      const cachedData: CachedData<T> = JSON.parse(cached);
      
      // CompanyId kontrolü
      if (cachedData.companyId !== companyId) {
        localStorage.removeItem(key);
        return null;
      }

      // Expire kontrolü
      const now = Date.now();
      if (now - cachedData.timestamp > CACHE_DURATION) {
        localStorage.removeItem(key);
        return null;
      }

      console.log(`✅ [CACHE] ${key} cache'den okundu`);
      return cachedData.data;
    } catch (error) {
      console.error(`❌ [CACHE] ${key} okuma hatası:`, error);
      localStorage.removeItem(key);
      return null;
    }
  }

  /**
   * Veriyi cache'e yazar
   */
  function setCachedData<T>(key: string, data: T, companyId: string): void {
    try {
      const cachedData: CachedData<T> = {
        data,
        timestamp: Date.now(),
        companyId
      };
      localStorage.setItem(key, JSON.stringify(cachedData));
      console.log(`✅ [CACHE] ${key} cache'e yazıldı`);
    } catch (error) {
      console.error(`❌ [CACHE] ${key} yazma hatası:`, error);
    }
  }

  /**
   * Şubeleri yükler (Firebase → Cache → Offline Fallback)
   * @param forceRefresh - true ise cache'i atlar ve Firebase'den yükler
   */
  async function loadBranches(forceRefresh: boolean = false): Promise<void> {
    const companyId = userDetails?.companyId;
    if (!companyId) {
      console.warn('[BRANCHES] companyId yok, yükleme atlandı');
      return;
    }

    const cacheKey = `gts_branches_${companyId}`;

    try {
      // 1. Cache'den dene (forceRefresh değilse)
      if (!forceRefresh) {
        const cached = getCachedData<Branch[]>(cacheKey, companyId);
        if (cached) {
          // Sadece varsayılan kayıt varsa cache'i stale sayıp yeniden yükle.
          // (Eski cache yüzünden yeni açılan şubeler görünmeyebiliyor.)
          if (cached.length > 1) {
            setBranches(cached);
            console.log(`✅ [BRANCHES] ${cached.length} şube cache'den yüklendi`);
            return;
          }
          console.log('🔁 [BRANCHES] Cache yalnızca varsayılan içeriyor, yeniden yüklenecek...');
        }
      } else {
        console.log('🔄 [BRANCHES] Force refresh - cache atlanıyor...');
        localStorage.removeItem(cacheKey);
      }

      // 2. branchDataService ile yükle (SADECE SQL — Faz 4 sonrasi Firebase yedegi yok)
      console.log('🔄 [BRANCHES] branchDataService ile yükleniyor (SQL-only)...');
      const branchesList: Branch[] = [];

      // "Şubeye Ait Değil" seçeneğini ekle (UI yardimci ogesi — servise dahil DEGIL)
      branchesList.push({
        id: "null",
        name: 'Şubeye Ait Değil'
      });

      const serviceBranches = await getBranchesAdapter(companyId);
      serviceBranches.forEach((b) => {
        branchesList.push({
          id: b.id,
          name: b.name || 'İsimsiz Şube',
          address: b.address || '',
          phone: b.phone || '',
          isHeadquarter: b.isHeadquarter || false
        });
      });

      setBranches(branchesList);
      setCachedData(cacheKey, branchesList, companyId);
      console.log(`✅ [BRANCHES] ${branchesList.length} şube yüklendi (branchDataService)`);
      
    } catch (error) {
      console.error('❌ [BRANCHES] Firebase yükleme hatası:', error);
      // Offline fallback: Cache'deki eski veriyi kullan (expire ignore)
      try {
        const oldCached = localStorage.getItem(cacheKey);
        if (oldCached) {
          const cachedData: CachedData<Branch[]> = JSON.parse(oldCached);
          if (cachedData.companyId === companyId) {
            setBranches(cachedData.data);
            console.log('⚠️ [BRANCHES] Offline: Eski cache kullanıldı');
          }
        }
      } catch (fallbackError) {
        console.error('❌ [BRANCHES] Offline fallback hatası:', fallbackError);
      }
    }
  }

  /**
   * Kategorileri yükler (SQL API → Cache → Offline Fallback)
   * @param forceRefresh - true ise cache'i atlar ve API'den yükler
   */
  async function loadCategories(forceRefresh: boolean = false): Promise<void> {
    const companyId = userDetails?.companyId;
    if (!companyId) {
      console.warn('[CATEGORIES] companyId yok, yükleme atlandı');
      return;
    }

    const cacheKey = `gts_categories_${companyId}`;

    try {
      // 1. Cache'den dene (forceRefresh değilse)
      if (!forceRefresh) {
        const cached = getCachedData<Category[]>(cacheKey, companyId);
        if (cached) {
          setCategories(cached);
          console.log(`✅ [CATEGORIES] ${cached.length} kategori cache'den yüklendi`);
          return;
        }
      } else {
        console.log('🔄 [CATEGORIES] Force refresh - cache atlanıyor...');
        localStorage.removeItem(cacheKey);
      }

      // 2. SQL API'den yükle
      console.log('🗄️ [CATEGORIES] SQL API\'den yükleniyor...');
      const response = await accountingApi.get(ACCOUNTING_API.INVENTORY_CATEGORIES);
      
      if (response.data && Array.isArray(response.data)) {
        const categoriesList: Category[] = response.data.map((data: any) => ({
          id: data.id,
          name: data.name || '',
          parentId: data.parentId || null,
          level: data.level || 1,
          createdAt: data.createdAt || new Date().toISOString(),
          branchesId: data.branchesId || null
        }));
        
        // Seviyeye göre sırala
        categoriesList.sort((a, b) => {
          if (a.level !== b.level) return a.level - b.level;
          return a.name.localeCompare(b.name);
        });
        
        setCategories(categoriesList);
        setCachedData(cacheKey, categoriesList, companyId);
        console.log(`✅ [CATEGORIES] ${categoriesList.length} kategori SQL'den yüklendi`);
      }
    } catch (error) {
      console.error('❌ [CATEGORIES] Yükleme hatası:', error);
      // Offline fallback
      try {
        const oldCached = localStorage.getItem(cacheKey);
        if (oldCached) {
          const cachedData: CachedData<Category[]> = JSON.parse(oldCached);
          if (cachedData.companyId === companyId) {
            setCategories(cachedData.data);
            console.log('⚠️ [CATEGORIES] Offline: Eski cache kullanıldı');
          }
        }
      } catch (fallbackError) {
        console.error('❌ [CATEGORIES] Offline fallback hatası:', fallbackError);
      }
    }
  }

  /**
   * Markaları yükler (SQL API → Cache → Offline Fallback)
   * @param forceRefresh - true ise cache'i atlar ve API'den yükler
   */
  async function loadBrands(forceRefresh: boolean = false): Promise<void> {
    const companyId = userDetails?.companyId;
    if (!companyId) {
      console.warn('[BRANDS] companyId yok, yükleme atlandı');
      return;
    }

    const cacheKey = `gts_brands_${companyId}`;

    try {
      // 1. Cache'den dene (forceRefresh değilse)
      if (!forceRefresh) {
        const cached = getCachedData<Brand[]>(cacheKey, companyId);
        if (cached) {
          setBrands(cached);
          console.log(`✅ [BRANDS] ${cached.length} marka cache'den yüklendi`);
          return;
        }
      } else {
        console.log('🔄 [BRANDS] Force refresh - cache atlanıyor...');
        localStorage.removeItem(cacheKey);
      }

      // 2. SQL API'den yükle
      console.log('🗄️ [BRANDS] SQL API\'den yükleniyor...');
      const response = await accountingApi.get(ACCOUNTING_API.INVENTORY_BRANDS);
      
      if (response.data && Array.isArray(response.data)) {
        const brandsList: Brand[] = response.data.map((data: any) => ({
          id: data.id,
          name: data.name || '',
          description: data.description || '',
          website: data.website || '',
        }));
        
        // İsme göre sırala
        brandsList.sort((a, b) => a.name.localeCompare(b.name));
        
        setBrands(brandsList);
        setCachedData(cacheKey, brandsList, companyId);
        console.log(`✅ [BRANDS] ${brandsList.length} marka SQL'den yüklendi`);
      }
    } catch (error) {
      console.error('❌ [BRANDS] Yükleme hatası:', error);
      // Offline fallback
      try {
        const oldCached = localStorage.getItem(cacheKey);
        if (oldCached) {
          const cachedData: CachedData<Brand[]> = JSON.parse(oldCached);
          if (cachedData.companyId === companyId) {
            setBrands(cachedData.data);
            console.log('⚠️ [BRANDS] Offline: Eski cache kullanıldı');
          }
        }
      } catch (fallbackError) {
        console.error('❌ [BRANDS] Offline fallback hatası:', fallbackError);
      }
    }
  }

  /**
   * Depoları yükler (SQL API → Cache → Offline Fallback)
   * @param forceRefresh - true ise cache'i atlar ve API'den yükler
   */
  async function loadWarehouses(forceRefresh: boolean = false): Promise<void> {
    const companyId = userDetails?.companyId;
    if (!companyId) {
      console.warn('[WAREHOUSES] companyId yok, yükleme atlandı');
      return;
    }

    const cacheKey = `gts_warehouses_${companyId}`;

    try {
      // 1. Cache'den dene (forceRefresh değilse)
      if (!forceRefresh) {
        const cached = getCachedData<Warehouse[]>(cacheKey, companyId);
        if (cached) {
          // Eski cache sürümlerinde warehouseType tutulmadığı için yanlış görünüm oluşabilir.
          // Bu durumda cache'i atlayıp API'den taze veri çek.
          const hasTypeInfo = cached.some(w => !!w.warehouseType);
          if (!hasTypeInfo) {
            console.log('♻️ [WAREHOUSES] Eski cache şeması tespit edildi, API’den tazeleniyor...');
            localStorage.removeItem(cacheKey);
          } else {
          setWarehouses(cached);
          console.log(`✅ [WAREHOUSES] ${cached.length} depo cache'den yüklendi`);
          return;
          }
        }
      } else {
        console.log('🔄 [WAREHOUSES] Force refresh - cache atlanıyor...');
        localStorage.removeItem(cacheKey);
      }

      // 2. SQL API'den yükle
      console.log('🗄️ [WAREHOUSES] SQL API\'den yükleniyor...');
      const response = await accountingApi.get(ACCOUNTING_API.INVENTORY_WAREHOUSES, {
        params: { includeItemCount: true }
      });
      
      if (response.data && Array.isArray(response.data)) {
        const warehousesList: Warehouse[] = response.data.map((data: any) => ({
          id: data.id || data.Id,
          name: data.name || data.Name || '',
          address: data.address || data.Address || '',
          description: data.description || data.Description || '',
          isDefault: data.isDefault ?? data.IsDefault ?? false,
          warehouseType: data.warehouseType || data.WarehouseType || 'Normal',
          locationModel: data.locationModel || data.LocationModel || undefined,
          createdAt: data.createdAt || data.CreatedAt || '',
          branchesId: data.branchesId || data.BranchesId || null,
          stockCount: data.stockCount ?? data.StockCount ?? 0,
          activeItemCount: data.itemCount ?? data.ItemCount ?? 0,
        }));
        
        // Varsayılan depoyu başa al ve isme göre sırala
        warehousesList.sort((a, b) => {
          if (a.isDefault && !b.isDefault) return -1;
          if (!a.isDefault && b.isDefault) return 1;
          return a.name.localeCompare(b.name);
        });
        
        setWarehouses(warehousesList);
        setCachedData(cacheKey, warehousesList, companyId);
        console.log(`✅ [WAREHOUSES] ${warehousesList.length} depo SQL'den yüklendi`);
      }
    } catch (error) {
      console.error('❌ [WAREHOUSES] Yükleme hatası:', error);
      // Offline fallback
      try {
        const oldCached = localStorage.getItem(cacheKey);
        if (oldCached) {
          const cachedData: CachedData<Warehouse[]> = JSON.parse(oldCached);
          if (cachedData.companyId === companyId) {
            setWarehouses(cachedData.data);
            console.log('⚠️ [WAREHOUSES] Offline: Eski cache kullanıldı');
          }
        }
      } catch (fallbackError) {
        console.error('❌ [WAREHOUSES] Offline fallback hatası:', fallbackError);
      }
    }
  }

  /**
   * ✅ Personel listesini yükler (Cloud Functions → Cache → Offline Fallback)
   */
  async function loadPersonnelList(): Promise<void> {
    const companyId = userDetails?.companyId;
    if (!companyId) {
      console.warn('[PERSONNEL] companyId yok, yükleme atlandı');
      return;
    }

    const cacheKey = `gts_personnel_${companyId}`;

    try {
      // 1. Cache'den dene
      const cached = getCachedData<PersonnelInfo[]>(cacheKey, companyId);
      if (cached) {
        setPersonnelList(cached);
        console.log(`✅ [PERSONNEL] ${cached.length} personel cache'den yüklendi`);
        return;
      }

      // 2. SADECE SQL (personnelDataService TEK KAYNAK; id=uid korunur). Faz 4 sonrasi Firebase yedegi yok.
      //    SqlOnly ise Firebase'e bakilmaz. Bu reroute, personnelList tuketen TUM
      //    bilesenleri otomatik SQL'e gecirir (mimari karar: tek kaynak katmanlama).
      console.log('🟦 [PERSONNEL] personnelDataService (SQL-once) ile yükleniyor...');
      const personnel = await getPersonnelAdapter(companyId);

      const personnelListData: PersonnelInfo[] = personnel
        .filter((p) => !p.isDeleted)
        .map((p) => ({
          id: p.id, // ✅ kanonik kimlik = uid
          name: p.name || 'İsimsiz Personel',
          email: p.email || '',
          phone: p.phone || '',
          role: p.role || '',
          branchesId: p.branchesId ?? null,
          departmentId: (p.departmentId as string | null | undefined) ?? null,
          companyId: companyId,
        }));

      // İsme göre sırala
      personnelListData.sort((a, b) => a.name.localeCompare(b.name));

      setPersonnelList(personnelListData);
      setCachedData(cacheKey, personnelListData, companyId);
      console.log(`✅ [PERSONNEL] ${personnelListData.length} personel yüklendi (SQL-once)`);
      
    } catch (error) {
      console.error('❌ [PERSONNEL] Yükleme hatası:', error);
      // Offline fallback - localStorage cache'i son care olarak dene
      try {
        const oldCached = localStorage.getItem(cacheKey);
        if (oldCached) {
          const cachedData: CachedData<PersonnelInfo[]> = JSON.parse(oldCached);
          if (cachedData.companyId === companyId) {
            setPersonnelList(cachedData.data);
            console.log('⚠️ [PERSONNEL] Offline: Eski cache kullanıldı');
          }
        }
      } catch (fallbackError) {
        console.error('❌ [PERSONNEL] Offline fallback hatası:', fallbackError);
      }
    }
  }

  /**
   * Helper fonksiyonlar
   */
  function getBranchName(branchId: string | null | undefined): string {
    if (!branchId || branchId === 'null') return 'Şubeye Ait Değil';
    const branch = branches.find(b => b.id === branchId);
    return branch ? branch.name : 'Şubeye Ait Değil';
  }

  function getCategoryName(categoryId: string | null | undefined): string {
    if (!categoryId) return '-';
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : '-';
  }

  function getBrandName(brandId: string | null | undefined): string {
    if (!brandId) return '-';
    const brand = brands.find(b => b.id === brandId);
    return brand ? brand.name : '-';
  }

  function getWarehouseName(warehouseId: string | null | undefined): string {
    if (!warehouseId) return '-';
    const warehouse = warehouses.find(w => w.id === warehouseId);
    return warehouse ? warehouse.name : '-';
  }

  /**
   * ✅ Personel bilgisi helper fonksiyonları
   */
  function getPersonnelInfo(personnelId: string): PersonnelInfo | null {
    if (!personnelId) return null;
    return personnelList.find(p => p.id === personnelId) || null;
  }

  function getPersonnelName(personnelId: string | null | undefined): string {
    if (!personnelId) return '-';
    const personnel = personnelList.find(p => p.id === personnelId);
    return personnel ? personnel.name : '-';
  }

  /**
   * Tüm cache'leri temizler
   */
  function clearAllCaches(): void {
    const companyId = userDetails?.companyId;
    if (!companyId) return;

    console.log('🗑️ [CACHE] Tüm cache\'ler temizleniyor...');
    localStorage.removeItem(`gts_branches_${companyId}`);
    localStorage.removeItem(`gts_categories_${companyId}`);
    localStorage.removeItem(`gts_brands_${companyId}`);
    localStorage.removeItem(`gts_warehouses_${companyId}`);
    localStorage.removeItem(`gts_personnel_${companyId}`);
    
    // State'leri de sıfırla
    setBranches([]);
    setCategories([]);
    setBrands([]);
    setWarehouses([]);
    setPersonnelList([]);
    
    console.log('✅ [CACHE] Tüm cache\'ler temizlendi');
  }

  /**
   * Tüm cache'leri yeniler (temizle + yeniden yükle)
   */
  async function refreshAllCaches(): Promise<void> {
    const companyId = userDetails?.companyId;
    if (!companyId) return;

    console.log('🔄 [CACHE] Tüm cache\'ler yenileniyor...');
    
    // Önce cache'leri temizle
    localStorage.removeItem(`gts_branches_${companyId}`);
    localStorage.removeItem(`gts_categories_${companyId}`);
    localStorage.removeItem(`gts_brands_${companyId}`);
    localStorage.removeItem(`gts_warehouses_${companyId}`);
    localStorage.removeItem(`gts_personnel_${companyId}`);
    
    // Yeniden yükle
    await Promise.all([
      loadBranches(),
      loadCategories(),
      loadBrands(),
      loadWarehouses(),
      loadPersonnelList()
    ]);
    
    console.log('✅ [CACHE] Tüm cache\'ler yenilendi');
  }

  useEffect(() => {
    // Kaydedilmiş kullanıcı detaylarını (userDetails) kontrol et — optimistik hydrate.
    const savedUserDetails = localStorage.getItem('userDetails');
    if (savedUserDetails) {
      try {
        setUserDetails(JSON.parse(savedUserDetails));
      } catch (e) {
        console.error("Kaydedilmiş kullanıcı verilerini ayrıştırma hatası:", e);
        localStorage.removeItem('userDetails');
      }
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // MÜŞTERİ EKRANI KONTROLÜ
    // Müşteri ekranında own oturum yoktur; listener eklemeye gerek yok.
    // ═══════════════════════════════════════════════════════════════════════════
    if (!auth) {
      console.log('🖥️ [AUTH] Auth null - Müşteri ekranı penceresi, oturum hydrate edilmeyecek');
      setLoading(false);
      setAuthReady(true);
      return () => {}; // Boş cleanup
    }

    let cancelled = false;

    // ── own-auth oturumunu hydrate et ──────────────────────────────────────────
    const hydrate = async () => {
      const storedUser = ownAuthSession.getStoredUser();
      if (storedUser) {
        // Önce optimistik shim ile state'i doldur (hızlı render).
        const authUser = buildAuthUser(storedUser);
        setCurrentUser(authUser);

        // Token'ı doğrula/yenile (gerekirse refresh). null → oturum geçersiz.
        const token = await ownAuthSession.getAccessToken();
        if (cancelled) return;
        if (!token) {
          console.warn('⚠️ [AUTH] Geçerli token yok / yenileme başarısız — oturum kapalı kabul ediliyor.');
          setCurrentUser(null);
          setUserDetails(null);
          localStorage.removeItem('userDetails');
          setLastLoadedCompanyId(null);
        }
      } else {
        console.log('🔐 [AUTH] Kayıtlı own-auth oturumu yok.');
        setCurrentUser(null);
      }

      if (cancelled) return;
      setLoading(false);
      setAuthReady(true);
      console.log('✅ [AUTH] authReady = true — API çağrıları artık güvenli');
    };

    void hydrate();

    // ── own-auth değişikliklerine abone ol (logout / refresh-failure) ──────────
    const unsubscribe = ownAuthSession.onAuthChange((reason) => {
      if (reason === 'logout' || reason === 'refresh-failed') {
        console.warn(`⚠️ [AUTH] ownAuthChange='${reason}' — state temizleniyor.`);
        setCurrentUser(null);
        setUserDetails(null);
        localStorage.removeItem('userDetails');
        setLastLoadedCompanyId(null);
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  // authReady olduktan sonra verileri şirket bazlı yükle
  const [lastLoadedCompanyId, setLastLoadedCompanyId] = useState<string | null>(null);
  
  useEffect(() => {
    if (!authReady || !userDetails?.companyId) return;

    const companyId = userDetails.companyId;
    if (lastLoadedCompanyId === companyId) return;

    console.log('🔄 [AUTH] Şirket değişimi algılandı, cache temizleniyor ve fresh veri çekiliyor...', {
      previousCompanyId: lastLoadedCompanyId,
      nextCompanyId: companyId
    });
    
    localStorage.removeItem(`gts_branches_${companyId}`);
    localStorage.removeItem(`gts_categories_${companyId}`);
    localStorage.removeItem(`gts_brands_${companyId}`);
    localStorage.removeItem(`gts_warehouses_${companyId}`);
    localStorage.removeItem(`gts_personnel_${companyId}`);

    // Önceki şirkete ait olabilecek veriyi anında temizle.
    setBranches([]);
    setCategories([]);
    setBrands([]);
    setWarehouses([]);
    setPersonnelList([]);

    // Firebase->SQL gecis modunu sirket bazli yukle (default: HybridFallback).
    // Hata/eksik alan durumunda guvenli tarafa (HybridFallback) duser.
    // Sirket degisiminde onceki sirketin modul kisitlamasi/demo durumu sizmasin.
    setEnabledModules(null);
    setDemoUntil(null);
    setDemoExpired(false);
    mainApi.get('/api/Company')
      .then((res) => {
        const mode = res?.data?.migrationMode as string | undefined;
        setMigrationMode(mode);
        setMigrationModeState(getMigrationMode());
        console.log('🧭 [AUTH] MigrationMode yuklendi:', getMigrationMode());

        // Modul kontrolu: dizi degilse null (kisitlama yok) — guvenli taraf.
        // demoExpired=true iken backend zaten bos dizi dondurur (moduller kilitli).
        const modules = res?.data?.enabledModules;
        setEnabledModules(Array.isArray(modules) ? modules : null);

        // Demo durumu: karar sunucunun demoExpired alanindadir (UTC hesap).
        setDemoUntil(typeof res?.data?.demoUntil === 'string' ? res.data.demoUntil : null);
        setDemoExpired(res?.data?.demoExpired === true);
      })
      .catch((err) => {
        setMigrationMode('HybridFallback');
        setMigrationModeState('HybridFallback');
        setEnabledModules(null);
        setDemoUntil(null);
        setDemoExpired(false);
        console.warn('⚠️ [AUTH] MigrationMode okunamadi, HybridFallback varsayildi:', err?.message);
      });

    Promise.all([
      loadBranches(true),
      loadCategories(true),
      loadBrands(true),
      loadWarehouses(true),
      loadPersonnelList()
    ]).then(() => {
      console.log('✅ [AUTH] Cache temizlendi ve veriler şirket bazlı yeniden yüklendi');
      setLastLoadedCompanyId(companyId);
    });
  }, [authReady, lastLoadedCompanyId, userDetails?.companyId]);

  // Alt admin izin haritasini yukle (deny-by-default: hata/bos kayit => {}).
  useEffect(() => {
    if (!currentUser?.uid || userDetails?.role !== 'subadmin') {
      setAdminPanelPermissions(null);
      setAdminPanelLoading(false);
      return;
    }
    let cancelled = false;
    setAdminPanelLoading(true);
    getAdminPanel(currentUser.uid)
      .then((res) => {
        if (cancelled) return;
        const settings =
          res.found && res.isEnabled && res.settings && typeof res.settings === 'object'
            ? (res.settings as AdminPermissionsMap)
            : {};
        setAdminPanelPermissions(settings);
      })
      .catch((err) => {
        console.warn('⚠️ [AUTH] adminPanel izinleri okunamadi (deny-by-default):', err?.message);
        if (!cancelled) setAdminPanelPermissions({});
      })
      .finally(() => {
        if (!cancelled) setAdminPanelLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentUser?.uid, userDetails?.role]);

  const value = {
    currentUser,
    userDetails,
    login,
    googleLogin,
    googleLoginPopup,
    googleRedirectResult,
    loginApple,
    logout,
    impersonate,
    stopImpersonation,
    isImpersonating: ownAuthSession.isImpersonating(),
    impersonationInfo: ownAuthSession.getImpersonationInfo(),
    loading,
    refreshUserDetails,
    setGoogleUserData: (userData: GoogleUserData) => setGoogleUserData(userData),
    googleUserData,
    companyName: userDetails?.companyName, // ✅ YENİ: Şirket adı
    // Merkezi Cache Sistemi
    branches,
    categories,
    brands,
    warehouses,
    loadBranches,
    loadCategories,
    loadBrands,
    loadWarehouses,
    getBranchName,
    getCategoryName,
    getBrandName,
    getWarehouseName,
    clearAllCaches,
    refreshAllCaches,
    // ✅ Personel Cache Sistemi
    personnelList,
    loadPersonnelList,
    getPersonnelInfo,
    getPersonnelName,
    migrationMode,
    enabledModules,
    isModuleEnabled: (moduleId: string) =>
      enabledModules == null || enabledModules.includes(moduleId),
    demoUntil,
    demoExpired,
    // Panel rol yardımcıları (bayi sistemi): UI görünürlüğü içindir; asıl yetki
    // her zaman backend'de doğrulanır (SuperAdminOnly / AdminScope guard'ları).
    isSuperAdmin: userDetails?.role === 'superadmin',
    isDealer: userDetails?.role === 'dealer',
    isPanelAdmin: userDetails?.role === 'superadmin' || userDetails?.role === 'dealer',
    isSubAdmin: userDetails?.role === 'subadmin',
    adminPanelPermissions,
    adminPanelLoading,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 